'use client'

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { InlineWidget, useCalendlyEventListener } from 'react-calendly'
import { cn } from '@nwwp/ui-shadcn'
import { useVeritonic } from '@nwwp/veritonic'
import { upsertAnonymousTrackingSnapshot } from '~/actions/upsert-anonymous-tracking-snapshot'
import { calendlyUsesAnonymousTrackingSnapshot, PAID_MEDIA_CALENDLY_URL, SCHEDULE_TRACKING_OPTIONS } from '~/utils/ctas'
import { getAnonymousId } from '~/utils/local-storage'
import { clearSchedulePrefill, loadCalendlyPrefill } from '~/utils/schedule-prefill'
import type { CalendlyUtm } from './buildCalendlyUrl'

type Props = {
  url: string
  utm?: CalendlyUtm
  tracking?: Record<string, string>
  /** Send the cookie anon id as utm.salesforce_uuid even for non-snapshot URLs (e.g. advisor bio embeds). */
  appendAnonymousId?: boolean
}

const emptySubscribe = () => () => {}

export const CalendlyScheduler = ({ url, utm, tracking = {}, appendAnonymousId = false }: Props) => {
  const { trackFlowComplete, trackScheduleMeeting } = useVeritonic()
  const [height, setHeight] = useState('500px')
  // sessionStorage doesn't exist during SSR, and rendering the widget first
  // without prefill would either cause a hydration mismatch or a visible iframe
  // reload once the prefill arrives — so gate the widget on client mount.
  const mounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  )
  // /schedule renders this component for two independent flows: the Schedule
  // Short Form handoff (PAID_MEDIA_CALENDLY_URL) and per-advisor "Schedule a
  // Meeting" links (buildCalendlyUrl.ts). The saved prefill belongs only to
  // the former — an abandoned short-form submission must never leak PII into
  // an unrelated advisor's widget later in the same tab.
  const isPaidMediaFlow = url === PAID_MEDIA_CALENDLY_URL
  const prefill = useMemo(
    () => (mounted && isPaidMediaFlow ? loadCalendlyPrefill() : undefined),
    [mounted, isPaidMediaFlow]
  )

  const usesAnonymousTrackingSnapshot = calendlyUsesAnonymousTrackingSnapshot(url)
  const injectsAnonymousId = usesAnonymousTrackingSnapshot || appendAnonymousId
  const anonymousId = mounted && injectsAnonymousId ? (getAnonymousId() ?? undefined) : undefined

  useEffect(() => {
    if (!mounted || !usesAnonymousTrackingSnapshot) return
    if (anonymousId && Object.keys(tracking).length > 0) {
      void upsertAnonymousTrackingSnapshot(anonymousId, tracking).catch(() => {})
    }
  }, [anonymousId, mounted, tracking, usesAnonymousTrackingSnapshot])

  const calendlyUtm = useMemo(() => {
    if (!injectsAnonymousId || !anonymousId) return utm
    return { ...utm, [SCHEDULE_TRACKING_OPTIONS.appendAnonymousIdKey]: anonymousId }
  }, [anonymousId, injectsAnonymousId, utm])

  // Clear the prefill on unmount (route change, tab close, abandonment) as
  // well as on successful booking, so it never outlives this render of the
  // paid-media flow.
  useEffect(() => {
    if (!isPaidMediaFlow) return
    return () => clearSchedulePrefill()
  }, [isPaidMediaFlow])

  useCalendlyEventListener({
    onPageHeightResize: (event) => setHeight(event.data.payload.height),
    onEventScheduled: () => {
      // Clear before tracking so a tracking throw can't leave PII behind.
      clearSchedulePrefill()
      trackFlowComplete()
      trackScheduleMeeting()
    },
  })

  return (
    <div className={cn('mt-6 flex w-full flex-col overflow-hidden rounded-lg', 'tablet:mt-0 tablet:overflow-visible')}>
      {mounted && (
        <InlineWidget
          url={url}
          styles={{ height, minHeight: 500 }}
          utm={calendlyUtm}
          prefill={prefill}
          pageSettings={{
            hideGdprBanner: true,
            hideLandingPageDetails: true,
          }}
        />
      )}
    </div>
  )
}
